import React from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import misc from '../misc.json';

const BASE_URL = 'https://tparan.am';

const SEO = ({
  title,
  description,
  keywords,
  image,
  type = 'website',
  productData = null
}) => {
  const { t, i18n } = useTranslation();
  let location;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    location = useLocation();
  } catch (e) {
    location = { pathname: '/' };
  }

  const currentLang = (i18n.language || 'hy').slice(0, 2);

  // Derive clean base path without /en, /ru, /hy prefix
  const rawPath = location ? location.pathname : '/';
  let basePath = rawPath.replace(/^\/(en|ru|hy)(\/|$)/, '/');
  if (!basePath.startsWith('/')) {
    basePath = '/' + basePath;
  }
  const cleanSubPath = basePath === '/' ? '' : basePath;

  // Language specific canonical and alternates
  const canonicalUrl = currentLang === 'hy'
    ? `${BASE_URL}${basePath}`
    : `${BASE_URL}/${currentLang}${cleanSubPath}`;

  const hyUrl = `${BASE_URL}${basePath}`;
  const enUrl = `${BASE_URL}/en${cleanSubPath}`;
  const ruUrl = `${BASE_URL}/ru${cleanSubPath}`;

  const pageTitle = title || t('title');
  const pageDescription = description || t('meta_description');
  const pageKeywords = keywords || misc.meta.keywords;
  const pageImage = image
    ? (image.startsWith('http') ? image : `${BASE_URL}${image}`)
    : `${BASE_URL}/uploads/home.webp`;

  // Locale codes for Open Graph
  const ogLocaleMap = {
    hy: 'hy_AM',
    en: 'en_US',
    ru: 'ru_RU'
  };
  const ogLocale = ogLocaleMap[currentLang] || 'hy_AM';
  const alternateLocales = Object.keys(ogLocaleMap)
    .filter((l) => l !== currentLang)
    .map((l) => ogLocaleMap[l]);

  // Schema.org Structured Data
  const businessSchema = {
    '@context': 'https://schema.org',
    '@type': 'PrintShop',
    '@id': `${BASE_URL}/#organization`,
    name: 'Tparan.am',
    alternateName: ['Տպարան.am', 'Типография Tparan.am', 'Тпаран.эйэм'],
    url: BASE_URL,
    logo: `${BASE_URL}/uploads/logo1.webp`,
    image: pageImage,
    description: pageDescription,
    telephone: ['+37460446685', '+37491407038', '+37493407038'],
    email: 'info@tparan.am',
    address: [
      {
        '@type': 'PostalAddress',
        streetAddress: 'Gyurjyan 15/25, Nor Nork',
        addressLocality: 'Yerevan',
        addressCountry: 'AM'
      },
      {
        '@type': 'PostalAddress',
        streetAddress: 'Arshakunyats 4, Press Building, 8th floor',
        addressLocality: 'Yerevan',
        addressCountry: 'AM'
      },
      {
        '@type': 'PostalAddress',
        streetAddress: 'IT Park Business Center',
        addressLocality: 'Yerevan',
        addressCountry: 'AM'
      }
    ],
    hasMap: [
      'https://yandex.com/maps/-/CXQIRWy-',
      'https://yandex.com/maps/-/CXQIZYK-',
      'https://yandex.com/maps/-/CXQIZBoY'
    ],
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 40.1905,
      longitude: 44.5592
    },
    sameAs: [
      'https://instagram.com/tparan.am',
      'https://facebook.com/tparan.am'
    ],
    knowsLanguage: ['hy', 'en', 'ru'],
    priceRange: '֏֏',
    currenciesAccepted: 'AMD',
    slogan: 'Մենք տպագրում ենք Ձեր մտքերը (We print your thoughts)',
    award: 'Best Printing and Publishing House in Armenia',
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.9',
      reviewCount: '380',
      bestRating: '5',
      worstRating: '1'
    }
  };

  const structuredDataList = [businessSchema];

  if (productData) {
    const productSchema = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: productData.name,
      description: productData.description || pageDescription,
      image: pageImage,
      category: 'Printing Services',
      offers: {
        '@type': 'Offer',
        priceCurrency: 'AMD',
        price: '0',
        availability: 'https://schema.org/InStock',
        seller: {
          '@type': 'PrintShop',
          name: 'Tparan.am'
        }
      }
    };
    structuredDataList.push(productSchema);
  }

  return (
    <Helmet>
      {/* HTML Lang */}
      <html lang={currentLang} />

      {/* Primary Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="title" content={pageTitle} />
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={pageKeywords} />
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />

      {/* Multilingual SEO Alternates & Canonical */}
      <link rel="canonical" href={canonicalUrl} />
      <link rel="alternate" hrefLang="hy" href={hyUrl} />
      <link rel="alternate" hrefLang="en" href={enUrl} />
      <link rel="alternate" hrefLang="ru" href={ruUrl} />
      <link rel="alternate" hrefLang="x-default" href={hyUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={pageImage} />
      <meta property="og:site_name" content="Tparan.am" />
      <meta property="og:locale" content={ogLocale} />
      {alternateLocales.map((loc) => (
        <meta key={loc} property="og:locale:alternate" content={loc} />
      ))}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={pageImage} />

      {/* AI & Search Engine JSON-LD Structured Data */}
      {structuredDataList.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
